import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  SettingsResponseSchema,
  SignatureUploadResponseSchema,
  type SettingsResponse,
  type SignatureUploadResponse,
  type UpdateSettingsInput,
} from '../../shared/settings.ts';

export const SETTINGS_QUERY_KEY = ['settings'] as const;

export interface UploadSignatureInput {
  senderProfileId: string;
  file: File;
}

export function useSettings(): UseQueryResult<SettingsResponse, Error> {
  return useQuery<SettingsResponse, Error>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: fetchSettings,
    retry: false,
  });
}

export function useUpdateSettings(): UseMutationResult<
  SettingsResponse,
  Error,
  UpdateSettingsInput
> {
  const queryClient = useQueryClient();

  return useMutation<SettingsResponse, Error, UpdateSettingsInput>({
    mutationFn: updateSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
  });
}

export function useUploadSignature(): UseMutationResult<
  SignatureUploadResponse,
  Error,
  UploadSignatureInput
> {
  const queryClient = useQueryClient();

  return useMutation<SignatureUploadResponse, Error, UploadSignatureInput>({
    mutationFn: uploadSignature,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
  });
}

async function fetchSettings(): Promise<SettingsResponse> {
  const response = await fetch('/api/settings', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `GET /api/settings failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return SettingsResponseSchema.parse(payload);
}

async function updateSettings(input: UpdateSettingsInput): Promise<SettingsResponse> {
  const payload = toUpdateSettingsPayload(input);
  const response = await fetch('/api/settings', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `PUT /api/settings failed with status ${response.status}`),
    );
  }

  const responsePayload: unknown = await response.json();
  return SettingsResponseSchema.parse(responsePayload);
}

function toUpdateSettingsPayload(input: UpdateSettingsInput): UpdateSettingsInput {
  return {
    senderName: input.senderName,
    nextInvoiceNumber: input.nextInvoiceNumber,
    notesTemplateEur: input.notesTemplateEur,
    notesTemplateUsd: input.notesTemplateUsd,
    notesTemplateRub: input.notesTemplateRub,
    bankRequisitesEur: input.bankRequisitesEur,
    bankRequisitesUsd: input.bankRequisitesUsd,
    bankRequisitesRub: input.bankRequisitesRub,
  };
}

async function uploadSignature(input: UploadSignatureInput): Promise<SignatureUploadResponse> {
  const formData = new FormData();
  formData.append('signature', input.file);

  const response = await fetch(
    `/api/sender-profiles/${encodeURIComponent(input.senderProfileId)}/signature`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error(
      await readApiError(response, `POST signature failed with status ${response.status}`),
    );
  }

  const payload: unknown = await response.json();
  return SignatureUploadResponseSchema.parse(payload);
}

async function readApiError(response: Response, fallback: string): Promise<string> {
  try {
    const payload: unknown = await response.json();
    if (isApiError(payload)) {
      return payload.error;
    }
  } catch {
    // Keep the original diagnostic fallback when the server returns non-JSON.
  }
  return fallback;
}

function isApiError(value: unknown): value is { error: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error?: unknown }).error === 'string'
  );
}
